/* eslint-disable array-callback-return */
import AddIcon from '@mui/icons-material/Add';
import { Button, Divider } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import { cloneDeep, concat, get, set } from 'lodash';
import React, { FC, useEffect, useMemo, useState } from 'react';
import CreateForm from 'src/components/ui-component/form-component/create-form/CreateForm';
import MUIAccordionComponent from 'src/components/ui-component/mui-accordion/MUIAccordionComponent';
import MUIAlert from 'src/components/ui-component/mui-alert/MUIAlert';
import { useApplication } from 'src/context/ApplicationContext';
import DeleteRecord from 'src/lib/api/DeleteRecord';
import GetRecords from 'src/lib/api/GetRecords';
import { APIS } from 'src/lib/constants/apis';
import { withKostenkategorieEinsatzbereichFilter } from 'src/utils/einsatzbereichHelper';
import { EMOB_LIS_STANDORTE_PATHS } from 'src/lib/constants/componentsPath';
import {
	ALS_ERLEDIGT_MARKIEREN_LABEL,
	AUSGABE_KATEGORIE,
	ERLAUTERUNG_IN_KOSTEN_TITLE,
	KOSTEN_ARTEN,
	KOSTEN_POSITION,
	KOSTENTRAEGER,
	PLATZHALTER_TEXT,
} from 'src/lib/constants/constant';
import { FormStrucItem, Validation } from 'src/lib/types/types';
import { ForceTrue, GetMaxDecimalWithStaticMinValidation, Required } from 'src/lib/validation/ValidationTypes';
import PruefAssistenz from 'src/pages/private/online-antrag/antrag-components/PruefAssistenz';
import ApplicationHeading from 'src/pages/private/online-antrag/application-heading/ApplicationHeading';
import { checkIfSpecificKostenPositionHasComment } from 'src/pages/private/online-antrag/application-helper-function/PruefAssistenzHelperFunktion';
import { getFormStrucComponentTable } from 'src/utils/formStruc';
import { addIndexesToPath, checkReadOnlyInUeberarbeitung, getOptionsFromRes, removeDataKeys, removeNullDeep } from 'src/utils/helper';
import {
	checkIfPruefAssitenzNeedsToValidate,
	getBasisDataKostenKategorie,
	getKostenErlauterung,
} from '../../../../../../application-helper-function/AppHelperfunctions';

// import schemas
import Schema from 'src/lib/paths/antrag_FULL_Paths.json';
import KostenKategorieSchema from 'src/lib/paths/kostenkategorie_FULL_Paths.json';
import KostenPositionSchema from 'src/lib/paths/kostenposition_FULL_Paths.json';
const P = Schema.attributes;
const KK_P = KostenKategorieSchema.attributes;
const KP_P = KostenPositionSchema.attributes;

const InvestitionKostenAntragEmobLis: FC<{ standortGroupId: string; hasComment?: boolean }> = ({ standortGroupId, hasComment }) => {
	const { application, setApplicationProperty, readOnly, saveApplication } = useApplication();
	const [kostenKategorie, setKostenKategorie] = useState([]);
	const updatedReadOnly = checkReadOnlyInUeberarbeitung(application, readOnly, hasComment);

	const unternehmenId = get(application, `${P.Antragstellende_Organisation.path}.id`, -1);

	const standOrtName = useMemo(() => {
		const alleStandorte_arr = get(
			application,
			P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Standort_Spezifikation_Ladeinfrastruktur.path,
			[],
		);
		const selectedStandort = alleStandorte_arr.find((ele: any) => get(ele, EMOB_LIS_STANDORTE_PATHS.GroupId, '') === standortGroupId);
		const standOrtName =
			get(selectedStandort, EMOB_LIS_STANDORTE_PATHS.StasseUndHausnummer, '') ?
				`(${get(selectedStandort, EMOB_LIS_STANDORTE_PATHS.StasseUndHausnummer, '')})`
			:	'';
		return standOrtName;
	}, [application, standortGroupId]);

	const handleAddInvestitionsKosten = async () => {
		let appCopy = cloneDeep(application);

		const data = getBasisDataKostenKategorie(
			appCopy,
			kostenKategorie, // All kostenkategorie for Investitionskosten, to find default Kostenkategorie id
			KOSTENTRAEGER.Antragsteller,
			unternehmenId,
			KOSTEN_POSITION.Ausgabe,
			KOSTEN_ARTEN.Investitionskosten,
		);

		set(data, 'Allgemein_Standort_Group_ID', standortGroupId);

		set(
			appCopy,
			addIndexesToPath(
				P.Foerderantrag_Kostenpositions.path,
				[`${get(appCopy, P.Foerderantrag_Kostenpositions.path, []).length}`],
				['Foerderantrag_Kostenpositions'],
			),
			{ attributes: data },
		);
		saveApplication(false, appCopy);
	};
	const handleDeleteInvestitionKosten = async (id: number) => {
		try {
			let appcopy = cloneDeep(application);
			const allKostenposition = get(appcopy, P.Foerderantrag_Kostenpositions.path, []);
			set(
				appcopy,
				P.Foerderantrag_Kostenpositions.path,
				allKostenposition.filter((item: any) => item.id !== id),
			);
			let res = await DeleteRecord(APIS.Kostenposition, id);
			if (res) {
				saveApplication(false, appcopy);
			}
		} catch (err: any) {
			console.error('ERROR: Deleting Personalkosten', err);
		}
	};

	// Fetch Kostenkategorie
	useEffect(() => {
		const fetch = async () => {
			try {
				const filter = withKostenkategorieEinsatzbereichFilter({
					Allgemein_Kostenposition: KOSTEN_POSITION.Ausgabe,
					Allgemein_Kostenart_Finanzierung: KOSTEN_ARTEN.Investitionskosten,
					Allgemein_Antragsformular: { $contains: get(application, P.Allgemein_Antragsformular.path) },
					Allgemein_Aktiv: { $eq: true },
				}, APIS.Antrag);
				let res = await GetRecords(APIS.Kostenkategorie, [], filter);
				setKostenKategorie(removeNullDeep(removeDataKeys(get(res, 'data', []))));
			} catch (err: any) {
				console.error('ERROR: Fetching Kostenkategorie', err);
			}
		};
		fetch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			<ApplicationHeading title={`Standort ${standOrtName}`} description={PLATZHALTER_TEXT} />
			{get(application, P.Foerderantrag_Kostenpositions.path, []).map((ele: any, i: number) => {
				if (
					get(ele, KP_P.Allgemein_Kostenposition.path) === KOSTEN_POSITION.Ausgabe &&
					get(ele, KP_P.Allgemein_Kostenart_Finanzierung.path) === KOSTEN_ARTEN.Investitionskosten &&
					get(ele, `${KP_P.Unternehmen.path}.id`) === unternehmenId &&
					get(ele, KP_P.Allgemein_Standort_Group_ID.path, '') === standortGroupId
				) {
					return (
						<MUIAccordionComponent
							key={i}
							summaryTitle={`Investitionskosten ${get(ele, KP_P.Allgemein_Bezeichnung.path, '') ? '| ' + get(ele, KP_P.Allgemein_Bezeichnung.path, '') : ''}`}
							onDelete={() => handleDeleteInvestitionKosten(get(ele, 'id'))}
							showDeleteButton={!updatedReadOnly}
							hasComment={checkIfSpecificKostenPositionHasComment(readOnly, ele)}
						>
							<PruefAssistenz
								statusPath={addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Pruefstatus.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								)}
								hinweisPath={addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Hinweis_Fuer_Antragsteller.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								)}
								ueberarbeitetAmPath={addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Ueberarbeitet_Am.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								)}
								erledigtPath={addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Erledigt.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								)}
								formStruc={mapInvestitionsKosten(PruefAssistenzStruc(), i)}
							/>

							<MUIAlert
								title={ERLAUTERUNG_IN_KOSTEN_TITLE}
								message={getKostenErlauterung(kostenKategorie, get(ele, KP_P.Allgemein_Kostenkategorie.path))}
							/>

							<Grid2 container spacing={6}>
								<Grid2 sm={6}>
									<CreateForm
										formStruc={mapInvestitionsKosten(formStrucKategorie(kostenKategorie), i)}
										formValues={application}
										updateFormValuesFunc={setApplicationProperty}
										readOnly={updatedReadOnly}
										api={APIS.Kostenposition}
									/>
								</Grid2>
							</Grid2>
							<Divider sx={{ mt: '2rem' }} />

							<CreateForm
								formStruc={mapInvestitionsKosten(formStrucKalkulation(application, i, kostenKategorie), i)}
								formValues={application}
								updateFormValuesFunc={setApplicationProperty}
								readOnly={updatedReadOnly}
								api={APIS.Kostenposition}
							/>

							<CreateForm
								formStruc={mapInvestitionsKosten(KostenProJahtStruc(application), i)}
								formValues={application}
								updateFormValuesFunc={setApplicationProperty}
								readOnly={updatedReadOnly}
								api={APIS.Kostenposition}
							/>
							<CreateForm
								formStruc={mapInvestitionsKosten(fomStrucGeplanteSumme(''), i)}
								formValues={application}
								updateFormValuesFunc={setApplicationProperty}
								readOnly={updatedReadOnly}
								api={APIS.Kostenposition}
							/>
						</MUIAccordionComponent>
					);
				}
			})}
			{unternehmenId && standortGroupId ?
				<Button sx={{ mt: '2rem' }} endIcon={<AddIcon />} onClick={handleAddInvestitionsKosten} disabled={updatedReadOnly}>
					Investitionskosten hinzufügen
				</Button>
			:	null}
		</>
	);
};

export default InvestitionKostenAntragEmobLis;

const PruefAssistenzStruc = () => {
	return [
		{
			fields: [
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Erledigt,
					label: ALS_ERLEDIGT_MARKIEREN_LABEL,
					validation: [{ validateFunction: ForceTrue }],
				},
			],
		},
	];
};

const formStrucKategorie = (kostenKategorie?: any): FormStrucItem[] => {
	const kategorieOptions = kostenKategorie || [];
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Kostenkategorie,
					type: 'enumeration',
					options: getOptionsFromRes(kategorieOptions, 'Allgemein_Bezeichnung'),
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};

const formStrucKalkulation = (application: any, index: number, kostenKategorie: any[]) => {
	const selectedAusgabenKategorie = getSelectedKostenkatorie(index, application, kostenKategorie);
	let struc: FormStrucItem[] = [];

	switch (selectedAusgabenKategorie) {
		case AUSGABE_KATEGORIE.NetzanschlussUndErdarbeiten:
			struc = [
				// {
				// 	fields: [
				// 		{
				// 			...P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Netzanschluss_Und_Erdarbeiten.schema.attributes
				// 				.Kalkulation_Netzanschluss_Und_Erdarbeiten_Kalkulierte_Ausgaben,
				// 			format: 'currency',
				// 		},
				// 	],
				// 	userProps: { direction: 'column', columnToDivide: 2 },
				// },
			];
			break;
		case AUSGABE_KATEGORIE.LadeTankInfrastruktur:
			struc = [
				{
					fields: [
						P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Ladeinfrastruktur_Lis.schema.attributes
							.Kalkulation_Ladeinfrastruktur_Art_Der_Ladeinfrastruktur,
						{
							...P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Ladeinfrastruktur_Lis.schema.attributes
								.Kalkulation_Ladeinfrastr_0267182084_ler_Bezeichnung_Ggf_Standort,
							label: 'Hersteller / Bezeichnung',
						},
					],
					userProps: { columnToDivide: 2 },
				},
			];
			break;
		case AUSGABE_KATEGORIE.Energiespeicher:
			struc = [
				// Das Feld kommt bestimmt wieder.
				// Daher erstmal nur auskommentieren, bis zur Abnahme
				// {
				// 	fields: [
				// 		{
				// 			...P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_LIS_Energiespeicher.schema.attributes
				// 				.Kalkulation_LIS_Energiespeicher_Speicherkapazitaet,
				// 			label: 'Speicherkapazität',
				// 		},
				// 	],
				// 	userProps: { columnToDivide: 1 },
				// },
			];
			break;
		default:
			struc = [];
	}

	return struc;
};

const KostenProJahtStruc = (application: any) => {
	return [
		{
			fields: [
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Kosten_Pro_Jahr,
					type: 'componentTable',
					maxLevel: get(application, P.Allgemein_Projektdauerin_Jahren.path),
					disabledColumns: ['Zelle_1'],
					fieldProps: { Zelle_2: { format: 'currency' } },
					hideDeleteButton: true,
				},
			],
			userProps: { columnToDivide: 2 },
		},
	];
};
const fomStrucGeplanteSumme = (selectedProjektbezogeneAusgabenPath: string) => {
	let validation: Validation[] = [];
	if (selectedProjektbezogeneAusgabenPath) {
		validation = [
			{ validateFunction: Required, disabledValidation: false },
			{
				validateFunction: GetMaxDecimalWithStaticMinValidation,
				params: [
					selectedProjektbezogeneAusgabenPath,
					'',
					'Der Vergleichswert darf nicht negativ sein (siehe Projektbezogene Ausgaben).',
					'Projektbezogene Ausgaben muss für einen Vergleich gesetzt werden.',
				],
				disabledValidation: false,
			},
		];
	}
	return [
		{
			fields: [
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Geplante_Gesamtsumme,
					format: 'currency',
					disabled: true,
					validation,
				},
			],
			userProps: { columnToDivide: 2 },
		},
	];
};

/**
 * ****************************************************************************************
 * ***************************** HELPER FUNCTIONS *****************************************
 * ****************************************************************************************
 */
const getSelectedKostenkatorie = (index: number, application: any, kostenKategorie: any[]) => {
	let selectecKatogorie = get(
		application,
		addIndexesToPath(
			P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Kostenkategorie.path,
			[`${index}`],
			['Foerderantrag_Kostenpositions'],
		),
	);

	if (typeof selectecKatogorie === 'object') {
		return get(selectecKatogorie, KK_P.Allgemein_Ausgabekategorie.path, '');
	} else {
		let kategorieObj = kostenKategorie.find((ele) => parseInt(get(ele, 'id')) === parseInt(selectecKatogorie));
		return get(kategorieObj, KK_P.Allgemein_Ausgabekategorie.path, '');
	}
};
const mapInvestitionsKosten = (formStruc: any[], index: number) => {
	let result = formStruc.map((row) => {
		return {
			...row,
			fields: (get(row, 'fields', []) as Array<any>).map((value) => {
				let pathWithoutIndex = get(value, 'path', '') as string;
				return {
					...value,
					path:
						pathWithoutIndex.slice(0, P.Foerderantrag_Kostenpositions.path.length) +
						`.${index}` +
						pathWithoutIndex.slice(P.Foerderantrag_Kostenpositions.path.length),
				};
			}),
		};
	});
	return result;
};
// const mapSelectedPath = (parentPath: string, childPath: string, index: number) => {
// 	const path = childPath.slice(0, parentPath.length) + `.${index}` + childPath.slice(parentPath.length);
// 	return path;
// };
export function getFormStrucInvestitionKostenAntragEmobLis(application: any, kostenKategorie: any, standortGroupId: string): FormStrucItem[] {
	let formStruc: FormStrucItem[] = [];
	const unternehmenId = get(application, `${P.Antragstellende_Organisation.path}.id`, -1);

	get(application, P.Foerderantrag_Kostenpositions.path, []).map((ele: any, i: number) => {
		if (
			get(ele, KP_P.Allgemein_Kostenposition.path) === KOSTEN_POSITION.Ausgabe &&
			get(ele, KP_P.Allgemein_Kostenart_Finanzierung.path) === KOSTEN_ARTEN.Investitionskosten &&
			get(ele, `${KP_P.Unternehmen.path}.id`) === unternehmenId &&
			get(ele, KP_P.Allgemein_Standort_Group_ID.path, '') === standortGroupId
		) {
			// let selectedKostenKategorie = getSelectedKostenkatorie(i, application, kostenKategorie);
			let selectedProjektbezogeneAusgabenPath = '';

			formStruc = concat(formStruc);

			// if (selectedKostenKategorie === AUSGABE_KATEGORIE.LadeTankInfrastruktur || selectedKostenKategorie === AUSGABE_KATEGORIE.Energiespeicher) {
			// 	selectedProjektbezogeneAusgabenPath = mapSelectedPath(
			// 		P.Foerderantrag_Kostenpositions.path,
			// 		P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Ladeinfrastruktur_Lis.schema.attributes
			// 			.Kalkulation_Ladeinfrastruktur_Projekbezogene_Ausgaben.path,
			// 		i
			// 	);
			// 	const ladeInfraStrucWithIndex = mapInvestitionsKosten(ladeInfraStruc(), i);
			// 	const ladeInfraStruc2WithIndex = mapInvestitionsKosten(ladeInfraStruc2(), i);
			// 	formStruc = concat(formStruc, ladeInfraStrucWithIndex, ladeInfraStruc2WithIndex);
			// }
			// if (selectedKostenKategorie === AUSGABE_KATEGORIE.NetzanschlussUndErdarbeiten) {
			// 	selectedProjektbezogeneAusgabenPath = mapSelectedPath(
			// 		P.Foerderantrag_Kostenpositions.path,
			// 		P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Netzanschluss_Und_Erdarbeiten.schema.attributes
			// 			.Kalkulation_Netzanschlus_0089722798_iten_Projekbezogene_Ausgaben.path,
			// 		i
			// 	);
			// 	const netzAnschlussStrucWithIndex = mapInvestitionsKosten(netzAnschlussStruc(), i);
			// 	const netzAnschlussStruc2WithIndex = mapInvestitionsKosten(netzAnschlussStruc2(), i);
			// 	formStruc = concat(formStruc, netzAnschlussStrucWithIndex, netzAnschlussStruc2WithIndex);
			// }
			const formStrucKategorieWithIndex = mapInvestitionsKosten(formStrucKategorie(), i);
			const formStrucKalkulateWithIndex = mapInvestitionsKosten(formStrucKalkulation(application, i, kostenKategorie), i);
			const formStrucCTWithIndex = getFormStrucComponentTable(application, mapInvestitionsKosten(KostenProJahtStruc(application), i));
			const formStrucGeplanteSummeWithIndex = mapInvestitionsKosten(fomStrucGeplanteSumme(selectedProjektbezogeneAusgabenPath), i);
			formStruc = concat(
				formStruc,
				formStrucCTWithIndex,
				formStrucKategorieWithIndex,
				formStrucKalkulateWithIndex,
				formStrucGeplanteSummeWithIndex,
			);

			const isHinweisFieldRequired = checkIfPruefAssitenzNeedsToValidate(ele, KP_P.Pruefung_Pruefstatus.path, KP_P.Pruefung_Erledigt.path);
			if (isHinweisFieldRequired) formStruc = concat(formStruc, mapInvestitionsKosten(PruefAssistenzStruc(), i));
		}
	});
	return formStruc;
}
